import React, { useState, useEffect } from 'react';
import { Box, Stack, Button, Typography, CircularProgress } from '@mui/material';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { fixGrammarAndSpell } from '../../../services/ai.service';

const stripHtml = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.innerText || div.textContent || '';
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'blockquote', 'code-block',
  'link',
];

const isEmpty = (html) => {
  if (!html) return true;
  const stripped = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
  return stripped === '';
};

const DescriptionSection = ({ initialDescription, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [description, setDescription] = useState(initialDescription);
  const [originalDescription, setOriginalDescription] = useState(initialDescription);
  const [isFixing, setIsFixing] = useState(false);
  const [fixed, setFixed] = useState(false);

  useEffect(() => {
    setDescription(initialDescription);
  }, [initialDescription]);

  const handleStartEditing = () => {
    setOriginalDescription(description);
    setIsEditing(true);
  };

  const handleSave = async () => {
    const result = await onSave({ description });
    if (result?.ok) setIsEditing(false);
  };

  const handleCancel = () => {
    setDescription(originalDescription);
    setIsEditing(false);
  };

  const handleFix = async () => {
    if (isFixing) return;
    const plain = stripHtml(description).trim();
    if (!plain) return;
    setIsFixing(true);
    try {
      const res = await fixGrammarAndSpell(plain);
      const corrected = res?.correctedText;
      if (corrected) {
        const lines = corrected.split('\n').filter((l) => l.trim());
        const html = lines.length > 1
          ? lines.map((l) => `<p>${l}</p>`).join('')
          : `<p>${corrected}</p>`;
        setDescription(html);
        setFixed(true);
        setTimeout(() => setFixed(false), 2000);
      }
    } catch (_) {
      // silently ignore
    } finally {
      setIsFixing(false);
    }
  };

  const hasContent = !isEmpty(description);

  return (
    <Box>
      {isEditing ? (
        <Box
          sx={{
            border: '2px solid #0D9488',
            borderRadius: '10px',
            overflow: 'hidden',
            boxShadow: '0 0 0 4px rgba(13,148,136,0.12)',
            background: '#fff',
            '& .ql-toolbar.ql-snow': {
              border: 'none',
              borderBottom: '1px solid #CBD5E1',
              background: '#F1F5F9',
              padding: '6px 10px',
            },
            '& .ql-toolbar .ql-stroke': { stroke: '#334155' },
            '& .ql-toolbar .ql-fill': { fill: '#334155' },
            '& .ql-toolbar button:hover .ql-stroke': { stroke: '#0D9488' },
            '& .ql-toolbar button:hover .ql-fill': { fill: '#0D9488' },
            '& .ql-toolbar button.ql-active .ql-stroke': { stroke: '#0D9488' },
            '& .ql-toolbar button.ql-active .ql-fill': { fill: '#0D9488' },
            '& .ql-toolbar .ql-picker-label': { color: '#334155' },
            '& .ql-container.ql-snow': {
              border: 'none',
              fontSize: '14px',
              fontFamily: "'Inter', sans-serif",
            },
            '& .ql-editor': {
              minHeight: '150px',
              maxHeight: '360px',
              overflowY: 'auto',
              color: '#1E293B',
              lineHeight: 1.75,
              padding: '14px 16px',
            },
            '& .ql-editor.ql-blank::before': {
              color: '#94A3B8',
              fontStyle: 'normal',
              fontSize: '14px',
              fontFamily: "'Inter', sans-serif",
            },
          }}
        >
          <ReactQuill
            value={description}
            onChange={setDescription}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="Add a detailed description..."
            theme="snow"
          />
        </Box>
      ) : (
        <Box
          onClick={handleStartEditing}
          sx={{
            border: '1.5px solid #E2E8F0',
            borderRadius: '10px',
            px: 2,
            py: 1.6,
            minHeight: 64,
            cursor: 'text',
            transition: 'all 0.15s ease',
            background: hasContent ? '#FAFBFC' : '#F8FAFC',
            overflow: 'hidden',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            '&:hover': {
              border: '1.5px solid #94A3B8',
              background: '#F1F5F9',
              boxShadow: '0 2px 8px rgba(15,23,42,0.07)',
            },
          }}
        >
          {hasContent ? (
            <Box
              sx={{
                fontSize: '14px',
                color: '#1E293B',
                lineHeight: 1.75,
                fontFamily: "'Inter', sans-serif",
                overflow: 'hidden',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                '& *': { maxWidth: '100%', boxSizing: 'border-box' },
                '& h1': { fontSize: '20px', fontWeight: 700, mb: '8px', mt: 0, color: '#0F172A' },
                '& h2': { fontSize: '17px', fontWeight: 600, mb: '6px', mt: 0, color: '#0F172A' },
                '& h3': { fontSize: '15px', fontWeight: 600, mb: '4px', mt: 0, color: '#0F172A' },
                '& p': { mb: '8px', mt: 0 },
                '& ul, & ol': { pl: '24px', mb: '8px' },
                '& li': { mb: '3px' },
                '& strong': { fontWeight: 700, color: '#0F172A' },
                '& em': { fontStyle: 'italic', color: '#475569' },
                '& u': { textDecorationColor: '#94A3B8' },
                '& s': { color: '#94A3B8' },
                '& code': {
                  background: '#E2E8F0',
                  borderRadius: '4px',
                  padding: '1px 6px',
                  fontSize: '13px',
                  fontFamily: "'Fira Code', 'Courier New', monospace",
                  color: '#DC2626',
                },
                '& pre': {
                  background: '#1E293B',
                  color: '#E2E8F0',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontFamily: "'Fira Code', 'Courier New', monospace",
                  overflowX: 'auto',
                  mb: '8px',
                },
                '& blockquote': {
                  borderLeft: '3px solid #0D9488',
                  paddingLeft: '14px',
                  margin: '0 0 8px 0',
                  color: '#475569',
                  fontStyle: 'italic',
                  background: '#F0FDFA',
                  borderRadius: '0 6px 6px 0',
                  padding: '8px 14px',
                },
                '& a': { color: '#0D9488', textDecoration: 'underline' },
              }}
            >
              {parse(DOMPurify.sanitize(description))}
            </Box>
          ) : (
            <Typography
              sx={{
                fontSize: '14px',
                color: '#94A3B8',
                fontFamily: "'Inter', sans-serif",
                lineHeight: 1.6,
                userSelect: 'none',
              }}
            >
              Add a detailed description...
            </Typography>
          )}
        </Box>
      )}

      {isEditing && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.2 }} alignItems="center">
          <Button
            onClick={handleSave}
            size="small"
            startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
            sx={{
              bgcolor: '#0D9488',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              borderRadius: '8px',
              px: 2.2,
              py: 0.6,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0B7A70', boxShadow: 'none' },
            }}
            variant="contained"
            disableElevation
          >
            Save
          </Button>
          <Button
            onClick={handleCancel}
            size="small"
            startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
            sx={{
              color: '#475569',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              borderRadius: '8px',
              px: 2.2,
              py: 0.6,
              border: '1.5px solid #CBD5E1',
              '&:hover': { border: '1.5px solid #FCA5A5', color: '#EF4444', bgcolor: '#FEF2F2' },
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleFix}
            disabled={isFixing || isEmpty(description)}
            size="small"
            startIcon={
              isFixing
                ? <CircularProgress size={12} sx={{ color: '#7C3AED' }} />
                : <AutoFixHighIcon sx={{ fontSize: 14 }} />
            }
            sx={{
              ml: 'auto !important',
              color: fixed ? '#16A34A' : '#7C3AED',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '12px',
              borderRadius: '8px',
              px: 1.8,
              py: 0.6,
              border: `1.5px solid ${fixed ? '#86EFAC' : '#DDD6FE'}`,
              bgcolor: fixed ? '#F0FDF4' : '#FAF5FF',
              transition: 'all 0.2s',
              '&:hover': { bgcolor: '#EDE9FE', color: '#6D28D9', border: '1.5px solid #C4B5FD' },
              '&:disabled': { opacity: 0.5 },
            }}
            variant="outlined"
          >
            {isFixing ? 'Fixing…' : fixed ? 'Fixed!' : 'Fix spelling & grammar'}
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default DescriptionSection;
